import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { oauthProviderLabel, type SsoInfoResponse } from '@verybigthings/trackframe-shared/schemas';
import { hub, hubBaseUrl } from '@/lib/hub-client';

/**
 * "Forgot password?" shows only when the hub says the flow can succeed
 * (reset routes mounted + a sending email provider). Unknown ⇒ hidden: a
 * link that leads nowhere is worse than none.
 */
export function showForgotPassword(info: Pick<SsoInfoResponse, 'passwordResetEnabled'> | null): boolean {
  return info?.passwordResetEnabled === true;
}

// Returns the validated `?return_to=` from the URL, or null. Server-side
// allowlist is the real fence; this just keeps obvious garbage out of the
// downstream redirect.
function readReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('return_to');
  if (!raw) return null;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  try {
    const u = new URL(raw);
    if (u.protocol === 'http:' || u.protocol === 'https:') return raw;
  } catch { /* noop */ }
  return null;
}

export function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [returnToRejected, setReturnToRejected] = useState(false);
  const requestedReturnTo = readReturnTo();

  // Validate return_to and silently redirect if the tf_sso cookie is already
  // valid. The form is rendered immediately — no spinner — so users who land
  // here unauthenticated never wait, and already-signed-in users (e.g. coming
  // back from an OAuth callback) just get redirected before they interact.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await hub.authSsoReturnTo({
          query: requestedReturnTo ? { return_to: requestedReturnTo } : {},
        });
        if (cancelled) return;
        if (!result.allowed) {
          setReturnTo(null);
          setReturnToRejected(true);
          setError('This sign-in destination is not allowed by the hub.');
          return;
        }
        const destination = result.returnTo ?? '/';
        setReturnTo(destination);
        await hub.authMe();
        if (!cancelled) window.location.replace(destination);
      } catch {
        // 401 from /auth/me, or any network error — just leave the form up.
      }
    })();
    return () => { cancelled = true; };
  }, [requestedReturnTo]);

  // Discover which OAuth providers are configured and whether a reset mail can be sent.
  useEffect(() => {
    let cancelled = false;
    hub.authSsoInfo()
      .then((info) => {
        if (cancelled) return;
        setOauthProviders(info.oauthProviders);
        setForgotPassword(showForgotPassword(info));
      })
      .catch(() => { /* fall back to empty list */ });
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (returnToRejected) {
        setError('This sign-in destination is not allowed by the hub.');
        setPending(false);
        return;
      }
      // Server sets tf_sso + tf_refresh on the response. The redirect
      // target receives them automatically because the cookie Domain
      // covers both the hub origin and the destination origin.
      await hub.authLogin({ body: { email, password } });
      window.location.replace(returnTo ?? '/');
    } catch (err) {
      setError((err as Error).message ?? 'Sign-in failed');
      setPending(false);
    }
  };

  const onOauth = async (provider: string) => {
    setError(null);
    try {
      const portalSelf = new URL(window.location.href);
      portalSelf.search = returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : '';
      portalSelf.hash = '';
      const startUrl = new URL(`${hubBaseUrl}/auth/oauth/${encodeURIComponent(provider)}/start`);
      startUrl.searchParams.set('redirectTo', portalSelf.toString());
      const res = await fetch(startUrl.toString(), { credentials: 'include' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message ?? 'OAuth start failed');
        return;
      }
      const data = await res.json();
      if (!data?.authorizationUrl) {
        setError('OAuth provider did not return an authorization URL');
        return;
      }
      window.location.href = data.authorizationUrl;
    } catch {
      setError('Network error contacting hub');
    }
  };

  if (returnToRejected) {
    return (
      <AuthShell title="Sign in" subtitle="Trackframe">
        <p className="text-sm text-destructive">
          This sign-in destination is not allowed by the hub.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Sign in" subtitle="Trackframe">
      {oauthProviders.length > 0 && (
        <div className="space-y-2">
          {oauthProviders.map((p) => (
            <Button
              key={p}
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => onOauth(p)}
            >
              Sign in with {oauthProviderLabel(p)}
            </Button>
          ))}
          <div className="relative my-2 text-center text-[11px] uppercase tracking-wider text-muted-foreground">
            <span className="bg-background px-2 relative z-10">or</span>
            <span className="absolute left-0 right-0 top-1/2 -z-0 h-px bg-border" />
          </div>
        </div>
      )}
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {forgotPassword && (
            <p className="text-right text-xs">
              <Link to="/login/forgot" className="text-muted-foreground underline-offset-4 hover:underline">
                Forgot password?
              </Link>
            </p>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  );
}
